import { makeStyles } from "@material-ui/core";
import { blue, grey } from "@material-ui/core/colors";

const useStyles = makeStyles(({ spacing, palette }) => ({
  accordion: {
    backgroundColor: "#2979FF14",
    borderRadius: spacing(1),
    boxShadow: "none",
    marginTop: 0,
    marginBottom: spacing(3),
    "&::before": {
      display: "none",
    },
    /**
     * Tripled on purpose: MUI's own
     * `.MuiAccordion-root.Mui-expanded:last-child {margin-bottom: 0}` is
     * specificity 0,3,0 and would drop the trailing gap on expand.
     */
    "&&&.Mui-expanded": {
      marginTop: 0,
      marginBottom: spacing(3),
    },
  },
  withTopMargin: {
    "&&&&, &&&&.Mui-expanded": {
      marginTop: spacing(3),
    },
  },
  noBottomMargin: {
    "&&&&, &&&&.Mui-expanded": {
      marginBottom: 0,
    },
  },
  summary: {
    padding: spacing(0, 1, 0, 1.5),
    minHeight: spacing(7),
    "&.Mui-expanded": {
      minHeight: spacing(7),
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: 0,
    },
  },
  summaryContent: {
    gap: spacing(1),
  },
  settingsBar: {
    backgroundColor: "#2979FF14",
    borderRadius: spacing(1),
    padding: spacing(0, 1, 0, 1.5),
    minHeight: spacing(7),
    marginBottom: spacing(3),
  },
  barTopMargin: {
    marginTop: spacing(2),
  },
  details: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: spacing(2),
    padding: spacing(0, 2, 2),
  },
  headerIcon: {
    width: spacing(3),
    height: spacing(3),
  },
  infoIcon: {
    width: 16,
    height: 16,
    color: palette.gray2.main,
  },
  settingsBarHeader: {
    minHeight: spacing(7),
  },
  settingsBarStacked: {
    paddingBottom: spacing(2),
  },
  languageCount: {
    color: palette.text.secondary,
  },
  languageCountEmphasis: {
    textDecoration: "underline",
  },
  groupLabel: {
    color: palette.text.primary,
  },
  labelChip: {
    height: 32,
    borderRadius: 24,
    backgroundColor: blue[50],
    border: `1px solid ${grey[400]}`,
    color: blue[900],
    "& .MuiChip-label": {
      padding: spacing(0, 1.5),
      fontSize: 14,
      fontWeight: 400,
      lineHeight: "19px",
      letterSpacing: "0.15px",
    },
  },
  sourceCaption: {
    backgroundColor: "transparent",
    border: `1px solid ${palette.divider}`,
    borderRadius: spacing(1),
    padding: spacing(1, 1.5),
    fontWeight: 600,
    "& .MuiSvgIcon-root": {
      color: palette.gray2.main,
    },
  },
  languageRow: {
    backgroundColor: palette.common.white,
  },
  overwriteModal: {
    width: 480,
    paddingBottom: spacing(2),
    "& > .MuiBox-root:last-child": {
      paddingTop: spacing(2),
    },
  },
  richTextHeader: {
    gap: spacing(1),
  },
  richTextSurface: {
    "& .ql-toolbar, & .ql-container": {
      backgroundColor: palette.common.white,
    },
  },
  richTextReadOnly: {
    backgroundColor: palette.common.white,
    border: `1px solid ${palette.divider}`,
    borderRadius: 4,
    padding: spacing(1.5, 2),
    minHeight: spacing(7),
    maxHeight: 320,
    overflowY: "auto",
  },
  previewBar: {
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    gap: spacing(2),
    padding: spacing(1.5, 2),
    borderRadius: spacing(1),
    backgroundColor: grey[50],
    boxShadow:
      "0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)",
  },
  previewChip: {
    display: "flex",
    height: 42,
    alignItems: "center",
    gap: spacing(1),
    padding: spacing(0.5, 1.5),
    borderRadius: spacing(1),
    backgroundColor: palette.common.white,
    border: `1px solid ${grey[300]}`,
    color: grey[800],
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    "&:focus-visible": {
      outline: `2px solid ${blue.A400}`,
      outlineOffset: 2,
    },
  },
  previewChipSelected: {
    borderColor: blue.A400,
    color: blue.A400,
  },
  aiButton: {
    padding: spacing(0.5),
  },
  footer: {
    gap: spacing(2),
  },
  autoFillButton: {
    whiteSpace: "nowrap",
  },
  expandAllButton: {
    whiteSpace: "nowrap",
  },
  conflictPaper: {
    width: 840,
    maxWidth: "100%",
    padding: spacing(3),
  },
  conflictTitle: {
    fontSize: 24,
    fontWeight: 500,
    lineHeight: 1.5,
    marginBottom: spacing(2),
  },
  conflictAlert: {
    marginBottom: spacing(2),
  },
  conflictEntries: {
    marginBottom: spacing(4),
  },
  conflictEntry: {
    display: "flex",
    flexDirection: "column",
    gap: spacing(1),
  },
  conflictHeading: {
    minHeight: spacing(5),
    paddingLeft: spacing(1.5),
  },
  conflictHeadingText: {
    color: palette.text.primary,
  },
  conflictShowMore: {
    whiteSpace: "nowrap",
  },
  conflictCandidates: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: spacing(2),
    paddingLeft: spacing(2),
  },
  conflictCandidate: {
    display: "flex",
    flexDirection: "column",
    gap: spacing(1),
    minWidth: 0,
  },
  conflictCandidateLabel: {
    fontSize: 14,
    fontWeight: 400,
  },
  conflictCandidateLabelSelected: {
    fontWeight: 500,
  },
  conflictCard: {
    display: "flex",
    flexDirection: "column",
    gap: spacing(2),
    padding: spacing(1.5, 2),
    borderRadius: spacing(1),
    backgroundColor: grey[50],
    marginLeft: spacing(5.5),
  },
  conflictCardSelected: {
    backgroundColor: "#2979FF14",
  },
  conflictLanguage: {
    padding: spacing(1),
  },
  conflictValue: {
    backgroundColor: palette.common.white,
    border: `1px solid ${grey[300]}`,
    borderRadius: 4,
    padding: spacing(1, 2),
    color: grey[700],
    wordBreak: "break-word",
  },
  conflictActions: {
    justifyContent: "flex-end",
  },
}));

export default useStyles;
